import { EnterForm } from '../components/EnterForm';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCustomers, getCustomerLedger, getUserLedger, getAllUsersBasic, updateUserTransfer, deleteUserTransfer, getEntryLedgers, createEntryLedger, updateEntryLedger, deleteEntryLedger, getEntryLedgerDetail, getEntryLedgerCategoryReport, bulkDeleteLedgerEntries, bulkDeleteEntryLedgers, getSuppliers, getSupplierLedgerSummary, getSupplierLedger } from '../lib/api';
import { formatCurrency, formatDate, getToday, getDateRange, formatApiErrorDetail } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SectionTiles } from '../components/SectionTiles';
import { User, Users, UserCog, Pencil, Trash2, AlertCircle, BookOpen, Plus, Building2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DateRangeFilter } from '../components/ui/date-range-filter';
import { ExportToolbar } from '../components/ui/export-toolbar';
import { Checkbox } from '../components/ui/checkbox';
import { FormShortcutHints } from '../components/ui/form-shortcut-hints';
import { toast } from 'sonner';

// Determine Indian Financial Year start year for a date string (YYYY-MM-DD)
function getFYStartYear(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const month = d.getMonth(); // 0-indexed, 3 = April
  const year = d.getFullYear();
  return month >= 3 ? year : year - 1;
}

function getFYLabel(startYear) {
  return `FY ${startYear}-${String(startYear + 1).slice(2)}`;
}

// Inject FY boundary rows into ledger entries when FY changes between consecutive entries
function injectFYBoundaries(entries) {
  if (!entries || entries.length === 0) return entries;
  const result = [];
  let prevFY = null;
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const currentFY = getFYStartYear(entry.date);
    if (prevFY !== null && currentFY !== null && currentFY !== prevFY) {
      // Get balance from previous entry (closing of old FY)
      const closingBal = i > 0 ? entries[i - 1].balance : 0;
      result.push({
        _fyBoundary: true,
        _boundaryType: 'closing',
        _fyLabel: getFYLabel(prevFY),
        balance: closingBal
      });
      result.push({
        _fyBoundary: true,
        _boundaryType: 'opening',
        _fyLabel: getFYLabel(currentFY),
        balance: closingBal
      });
    }
    prevFY = currentFY;
    result.push(entry);
  }
  return result;
}

const Ledger = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const initialTab = location.state?.tab || '';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Listen for quick-open tab switch events
  useEffect(() => {
    const handleQuickOpenTab = (e) => {
      const tab = e.detail?.tab;
      if (tab && ['customer-ledger', 'user-ledger', 'supplier-ledger', 'entry-ledger'].includes(tab)) setActiveTab(tab);
    };
    window.addEventListener('quickopen-tab', handleQuickOpenTab);
    return () => window.removeEventListener('quickopen-tab', handleQuickOpenTab);
  }, []);

  const LEDGER_TABS = ['customer-ledger', 'user-ledger', 'supplier-ledger', 'entry-ledger'];
  const switchLedgerTab = (direction) => {
    setActiveTab(t => {
      const i = LEDGER_TABS.indexOf(t);
      const next = direction === 'next'
        ? LEDGER_TABS[(i + 1) % LEDGER_TABS.length]
        : LEDGER_TABS[(i - 1 + LEDGER_TABS.length) % LEDGER_TABS.length];
      setSelectedCustIds([]); setSelectedUserIds([]); setSelectedEntryIds([]); setSelectedLedgerIds([]);
      return next;
    });
  };
  usePageCallbacks({
    onNewEntry: () => setEntryLedgerDialogOpen(true),
    onPrevTab: () => switchLedgerTab('prev'),
    onNextTab: () => switchLedgerTab('next'),
    onDateQuick: (key) => {
      const range = getDateRange(key);
      if (activeTab === 'customer-ledger') {
        setCustDateRange({ start_date: range.start_date, end_date: range.end_date });
        setCustQuick(key);
        if (selectedCustomer) fetchCustomerLedger(selectedCustomer, range.start_date, range.end_date);
      } else if (activeTab === 'user-ledger') {
        setUserDateRange({ start_date: range.start_date, end_date: range.end_date });
        setUserQuick(key);
        if (selectedUser) fetchUserLedger(selectedUser, range.start_date, range.end_date);
      } else if (activeTab === 'supplier-ledger') {
        setSupplierDateRange({ start_date: range.start_date, end_date: range.end_date });
        setSupplierQuick(key);
        if (selectedSupplier) fetchSupplierLedger(selectedSupplier, range.start_date, range.end_date);
      } else if (activeTab === 'entry-ledger') {
        setEntryLedgerQuick(key);
      }
    },
  });
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [customerLedger, setCustomerLedger] = useState(null);
  const [userLedger, setUserLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [userLedgerLoading, setUserLedgerLoading] = useState(false);

  // Transfer edit state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [transferForm, setTransferForm] = useState({ from_user_id: '', to_user_id: '', amount: '', remarks: '' });
  const [transferError, setTransferError] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  // Entry Ledger state
  const [entryLedgers, setEntryLedgers] = useState([]);
  const [selectedEntryLedger, setSelectedEntryLedger] = useState('');
  const [focusedEntryLedger, setFocusedEntryLedger] = useState('');
  const [entryLedgerData, setEntryLedgerData] = useState(null);
  const [entryLedgerLoading, setEntryLedgerLoading] = useState(false);
  const [entryLedgerDialogOpen, setEntryLedgerDialogOpen] = useState(false);
  const [editingEntryLedger, setEditingEntryLedger] = useState(null);
  const [entryLedgerForm, setEntryLedgerForm] = useState({ name: '', category: '' });
  const [entryLedgerError, setEntryLedgerError] = useState('');
  const [entryLedgerSubmitting, setEntryLedgerSubmitting] = useState(false);
  const [entryLedgerCategories, setEntryLedgerCategories] = useState([]);
  const [selectedEntryLedgerCategory, setSelectedEntryLedgerCategory] = useState('');
  const [categoryReport, setCategoryReport] = useState(null);
  const [categoryReportLoading, setCategoryReportLoading] = useState(false);

  // Multi-select state for ledger entries
  const [selectedCustIds, setSelectedCustIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [selectedLedgerIds, setSelectedLedgerIds] = useState([]);

  // Supplier Ledger state
  const [supplierList, setSupplierList] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [supplierLedger, setSupplierLedger] = useState(null);
  const [supplierLedgerLoading, setSupplierLedgerLoading] = useState(false);
  const [supplierDateRange, setSupplierDateRange] = useState({ start_date: '', end_date: '' });
  const [supplierQuick, setSupplierQuick] = useState('full-ledger');
  const [selectedSuppIds, setSelectedSuppIds] = useState([]);

  const currentMonth = getDateRange('current-month');
  const [custDateRange, setCustDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [userDateRange, setUserDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [entryLedgerDateRange, setEntryLedgerDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [custQuick, setCustQuick] = useState('current-month');
  const [userQuick, setUserQuick] = useState('current-month');
  const [entryLedgerQuick, setEntryLedgerQuick] = useState('current-month');

  // Mark the correct filter field for the F shortcut based on active tab
  useEffect(() => {
    const filterMap = {
      'customer-ledger': '[data-testid="ledger-customer-select"]',
      'user-ledger': '[data-testid="ledger-user-select"]',
      'entry-ledger': '[data-testid^="entry-ledger-row-"]',
    };
    const selector = filterMap[activeTab];
    if (selector) {
      setTimeout(() => {
        document.querySelectorAll('[data-shortcut-filter]').forEach(e => e.removeAttribute('data-shortcut-filter'));
        const el = document.querySelector(selector);
        if (el) el.setAttribute('data-shortcut-filter', 'true');
      }, 50);
    }
  }, [activeTab]);

  // Listen for F shortcut on entry-ledger tab to set focus cursor
  useEffect(() => {
    if (activeTab !== 'entry-ledger') return;
    const handler = (e) => {
      const row = e.target.closest('[data-testid^="entry-ledger-row-"]');
      if (row) {
        const id = row.getAttribute('data-testid').replace('entry-ledger-row-', '');
        setFocusedEntryLedger(id);
      }
    };
    document.addEventListener('shortcut-focus', handler);
    return () => document.removeEventListener('shortcut-focus', handler);
  }, [activeTab]);


  // Arrow key navigation for entry ledger rows + Enter to open
  useEffect(() => {
    if (activeTab !== 'entry-ledger') return;
    const handler = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const rows = Array.from(document.querySelectorAll('[data-testid^="entry-ledger-row-"]'));
        if (rows.length === 0) return;
        e.preventDefault();

        const ids = rows.map(r => r.getAttribute('data-testid').replace('entry-ledger-row-', ''));
        const currentIdx = ids.indexOf(focusedEntryLedger);
        let nextIdx;
        if (e.key === 'ArrowDown') {
          nextIdx = currentIdx < 0 ? 0 : Math.min(currentIdx + 1, ids.length - 1);
        } else {
          nextIdx = currentIdx <= 0 ? 0 : currentIdx - 1;
        }
        setFocusedEntryLedger(ids[nextIdx]);
        rows[nextIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (e.key === 'Enter' && focusedEntryLedger) {
        e.preventDefault();
        handleEntryLedgerSelect(focusedEntryLedger);
        return;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [activeTab, focusedEntryLedger]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    fetchInitialData();
  }, []);

  // Auto-select user when navigated from Dashboard user card
  useEffect(() => {
    if (location.state?.userId && users.length > 0) {
      const userId = location.state.userId;
      setSelectedUser(userId);
      fetchUserLedger(userId, userDateRange.start_date, userDateRange.end_date);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const fetchInitialData = async () => {
    try {
      const [customersRes, usersRes, ledgersRes, categoryRes, suppliersRes] = await Promise.all([
        getCustomers(),
        getAllUsersBasic(),
        getEntryLedgers(),
        getEntryLedgerCategoryReport(),
        getSuppliers()
      ]);
      setCustomers(customersRes.data);
      setUsers(usersRes.data);
      setEntryLedgers(ledgersRes.data);
      setCategoryReport(categoryRes.data);
      setEntryLedgerCategories(categoryRes.data.categories || []);
      setSupplierList(suppliersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerLedger = async (customerId, startDate, endDate) => {
    if (!customerId) { setCustomerLedger(null); return; }
    setLedgerLoading(true);
    try {
      const res = await getCustomerLedger(customerId, startDate, endDate);
      setCustomerLedger(res.data);
    } catch (error) {
      toast.error('Failed to fetch customer ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  const fetchUserLedger = async (userId, startDate, endDate) => {
    if (!userId) { setUserLedger(null); return; }
    setUserLedgerLoading(true);
    try {
      const res = await getUserLedger(userId, startDate, endDate);
      setUserLedger(res.data);
    } catch (error) {
      toast.error('Failed to fetch user ledger');
    } finally {
      setUserLedgerLoading(false);
    }
  };

  // Reset selections on data changes
  const handleCustomerSelect = (customerId) => {
    setSelectedCustomer(customerId);
    setSelectedCustIds([]);
    fetchCustomerLedger(customerId, custDateRange.start_date, custDateRange.end_date);
  };

  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
    setSelectedUserIds([]);
    fetchUserLedger(userId, userDateRange.start_date, userDateRange.end_date);
  };

  const handleCustDateApply = (start, end) => {
    setCustDateRange({ start_date: start, end_date: end });
    setSelectedCustIds([]);
    if (selectedCustomer) fetchCustomerLedger(selectedCustomer, start, end);
  };

  const handleUserDateApply = (start, end) => {
    setUserDateRange({ start_date: start, end_date: end });
    setSelectedUserIds([]);
    if (selectedUser) fetchUserLedger(selectedUser, start, end);
  };

  // Supplier Ledger
  const fetchSupplierLedger = async (supplierId, startDate, endDate) => {
    if (!supplierId) { setSupplierLedger(null); return; }
    setSupplierLedgerLoading(true);
    try { const res = await getSupplierLedger(supplierId, startDate, endDate); setSupplierLedger(res.data); }
    catch (e) { toast.error('Failed to load supplier ledger'); }
    finally { setSupplierLedgerLoading(false); }
  };
  const handleSupplierSelect = (supplierId) => {
    setSelectedSupplier(supplierId);
    fetchSupplierLedger(supplierId, supplierDateRange.start_date, supplierDateRange.end_date);
  };
  const handleSupplierDateApply = (start, end) => {
    setSupplierDateRange({ start_date: start, end_date: end });
    if (selectedSupplier) fetchSupplierLedger(selectedSupplier, start, end);
  };
  const supplierOptions = supplierList.map(s => ({ value: s.id, label: s.company_name }));

  // Supplier ledger entries + FY boundaries
  const supplierLedgerEntries = supplierLedger?.ledger || [];
  const supplierLedgerWithFY = injectFYBoundaries(supplierLedgerEntries);
  const allSuppSelected = supplierLedgerEntries.length > 0 && supplierLedgerEntries.every(e => selectedSuppIds.includes(e.reference_id));
  const toggleSuppSelect = (id) => setSelectedSuppIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllSupp = () => { if (allSuppSelected) setSelectedSuppIds([]); else setSelectedSuppIds(supplierLedgerEntries.map(e => e.reference_id)); };

  const handleBulkDeleteSupp = async () => {
    if (!window.confirm(`Delete ${selectedSuppIds.length} selected entries?`)) return;
    try {
      await bulkDeleteLedgerEntries(selectedSuppIds);
      toast.success(`Deleted ${selectedSuppIds.length} entries`);
      setSelectedSuppIds([]);
      fetchSupplierLedger(selectedSupplier, supplierDateRange.start_date, supplierDateRange.end_date);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  // Customer ledger multi-select
  const custLedgerEntries = customerLedger?.ledger || [];
  const allCustSelected = custLedgerEntries.length > 0 && custLedgerEntries.every(e => selectedCustIds.includes(e.reference_id));
  const toggleCustSelect = (id) => setSelectedCustIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllCust = () => { if (allCustSelected) setSelectedCustIds([]); else setSelectedCustIds(custLedgerEntries.map(e => e.reference_id)); };

  // User ledger multi-select
  const userLedgerEntries = userLedger?.ledger || [];
  const allUserSelected = userLedgerEntries.length > 0 && userLedgerEntries.every(e => selectedUserIds.includes(e.reference_id));
  const toggleUserSelect = (id) => setSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllUser = () => { if (allUserSelected) setSelectedUserIds([]); else setSelectedUserIds(userLedgerEntries.map(e => e.reference_id)); };

  // Entry ledger multi-select
  const entryLedgerEntries = entryLedgerData?.ledger || [];
  const allEntrySelected = entryLedgerEntries.length > 0 && entryLedgerEntries.every(e => selectedEntryIds.includes(e.reference_id));
  const toggleEntrySelect = (id) => setSelectedEntryIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllEntry = () => { if (allEntrySelected) setSelectedEntryIds([]); else setSelectedEntryIds(entryLedgerEntries.map(e => e.reference_id)); };

  // Entry ledger LIST multi-select (for the ledger names themselves)
  const allLedgersSelected = entryLedgers.length > 0 && entryLedgers.every(l => selectedLedgerIds.includes(l.id));
  const toggleLedgerSelect = (id) => setSelectedLedgerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllLedgers = () => { if (allLedgersSelected) setSelectedLedgerIds([]); else setSelectedLedgerIds(entryLedgers.map(l => l.id)); };

  // FY boundary enriched entries
  const custLedgerWithFY = injectFYBoundaries(custLedgerEntries);
  const userLedgerWithFY = injectFYBoundaries(userLedgerEntries);
  const entryLedgerWithFY = injectFYBoundaries(entryLedgerEntries);

  // Bulk delete handlers
  const handleBulkDeleteCust = async () => {
    if (!window.confirm(`Delete ${selectedCustIds.length} selected entries?`)) return;
    try {
      await bulkDeleteLedgerEntries(selectedCustIds);
      toast.success(`Deleted ${selectedCustIds.length} entries`);
      setSelectedCustIds([]);
      fetchCustomerLedger(selectedCustomer, custDateRange.start_date, custDateRange.end_date);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const handleBulkDeleteUser = async () => {
    if (!window.confirm(`Delete ${selectedUserIds.length} selected entries?`)) return;
    try {
      await bulkDeleteLedgerEntries(selectedUserIds);
      toast.success(`Deleted ${selectedUserIds.length} entries`);
      setSelectedUserIds([]);
      fetchUserLedger(selectedUser, userDateRange.start_date, userDateRange.end_date);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const handleBulkDeleteEntry = async () => {
    if (!window.confirm(`Delete ${selectedEntryIds.length} selected entries?`)) return;
    try {
      await bulkDeleteLedgerEntries(selectedEntryIds);
      toast.success(`Deleted ${selectedEntryIds.length} entries`);
      setSelectedEntryIds([]);
      fetchEntryLedgerDetail(selectedEntryLedger, entryLedgerDateRange.start_date, entryLedgerDateRange.end_date);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const handleBulkDeleteLedgers = async () => {
    if (!window.confirm(`Delete ${selectedLedgerIds.length} selected entry ledgers?`)) return;
    try {
      await bulkDeleteEntryLedgers(selectedLedgerIds);
      toast.success(`Deleted ${selectedLedgerIds.length} entry ledgers`);
      setSelectedLedgerIds([]);
      fetchEntryLedgers();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  // Entry Ledger handlers
  const fetchEntryLedgerDetail = async (ledgerId, startDate, endDate) => {
    if (!ledgerId) { setEntryLedgerData(null); return; }
    setEntryLedgerLoading(true);
    try {
      const res = await getEntryLedgerDetail(ledgerId, startDate, endDate);
      setEntryLedgerData(res.data);
    } catch (error) {
      toast.error('Failed to fetch entry ledger');
    } finally {
      setEntryLedgerLoading(false);
    }
  };

  const handleEntryLedgerSelect = (ledgerId) => {
    setSelectedEntryLedger(ledgerId);
    setSelectedEntryIds([]);
    fetchEntryLedgerDetail(ledgerId, entryLedgerDateRange.start_date, entryLedgerDateRange.end_date);
  };

  const handleEntryLedgerDateApply = (start, end) => {
    setEntryLedgerDateRange({ start_date: start, end_date: end });
    setSelectedEntryIds([]);
    if (selectedEntryLedger) fetchEntryLedgerDetail(selectedEntryLedger, start, end);
  };

  const handleCategoryFilter = async (category) => {
    setSelectedEntryLedgerCategory(category);
    setCategoryReportLoading(true);
    try {
      const res = await getEntryLedgerCategoryReport(category);
      setCategoryReport(res.data);
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setCategoryReportLoading(false);
    }
  };

  const handleEntryLedgerSubmit = async (e) => {
    e.preventDefault(); setEntryLedgerError(''); setEntryLedgerSubmitting(true);
    try {
      if (editingEntryLedger) {
        await updateEntryLedger(editingEntryLedger.id, entryLedgerForm);
        toast.success('Entry ledger updated');
      } else {
        await createEntryLedger(entryLedgerForm);
        toast.success('Entry ledger created');
      }
      setEntryLedgerDialogOpen(false);
      setEditingEntryLedger(null);
      setEntryLedgerForm({ name: '', category: '' });
      const [ledgersRes, categoryRes] = await Promise.all([
        getEntryLedgers(),
        getEntryLedgerCategoryReport(selectedEntryLedgerCategory || undefined)
      ]);
      setEntryLedgers(ledgersRes.data);
      setCategoryReport(categoryRes.data);
      setEntryLedgerCategories(categoryRes.data.categories || []);
    } catch (err) {
      setEntryLedgerError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setEntryLedgerSubmitting(false); }
  };

  const handleDeleteEntryLedger = async (ledger) => {
    if (!window.confirm(`Delete entry ledger "${ledger.name}"?`)) return;
    try {
      await deleteEntryLedger(ledger.id);
      toast.success('Entry ledger deleted');
      if (selectedEntryLedger === ledger.id) { setSelectedEntryLedger(''); setEntryLedgerData(null); }
      const [ledgersRes, categoryRes] = await Promise.all([
        getEntryLedgers(),
        getEntryLedgerCategoryReport(selectedEntryLedgerCategory || undefined)
      ]);
      setEntryLedgers(ledgersRes.data);
      setCategoryReport(categoryRes.data);
      setEntryLedgerCategories(categoryRes.data.categories || []);
    } catch (err) { toast.error('Failed to delete'); }
  };

  const openEditEntryLedger = (ledger) => {
    setEditingEntryLedger(ledger);
    setEntryLedgerForm({ name: ledger.name, category: ledger.category || '' });
    setEntryLedgerError('');
    setEntryLedgerDialogOpen(true);
  };

  const openCreateEntryLedger = () => {
    setEditingEntryLedger(null);
    setEntryLedgerForm({ name: '', category: '' });
    setEntryLedgerError('');
    setEntryLedgerDialogOpen(true);
  };

  // Transfer edit/delete handlers
  const openTransferEdit = (entry) => {
    setEditingTransfer(entry);
    setTransferForm({
      from_user_id: entry.from_user_id || '',
      to_user_id: entry.to_user_id || '',
      amount: entry.raw_amount?.toString() || '',
      remarks: entry.description || ''
    });
    setTransferError('');
    setTransferDialogOpen(true);
  };

  const handleTransferUpdate = async (e) => {
    e.preventDefault();
    setTransferError('');
    setTransferSubmitting(true);
    try {
      await updateUserTransfer(editingTransfer.entry_id, {
        from_user_id: transferForm.from_user_id,
        to_user_id: transferForm.to_user_id,
        amount: parseFloat(transferForm.amount),
        remarks: transferForm.remarks
      });
      toast.success('Transfer updated');
      setTransferDialogOpen(false);
      fetchUserLedger(selectedUser, userDateRange.start_date, userDateRange.end_date);
    } catch (err) {
      setTransferError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleTransferDelete = async (entry) => {
    if (!window.confirm('Delete this transfer entry?')) return;
    try {
      await deleteUserTransfer(entry.entry_id);
      toast.success('Transfer deleted');
      fetchUserLedger(selectedUser, userDateRange.start_date, userDateRange.end_date);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const customerOptions = customers.map(c => ({
    value: c.id,
    label: c.company_name || c.name
  }));

  const userOptions = users.map(u => ({
    value: u.id,
    label: u.name
  }));

  if (loading && activeTab) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const SECTION_DEFS = [
    { key: '1', value: 'customer-ledger', label: 'Customer', icon: Users },
    { key: '2', value: 'user-ledger', label: 'User', icon: UserCog },
    { key: '3', value: 'supplier-ledger', label: 'Supplier', icon: Building2 },
    { key: '4', value: 'entry-ledger', label: 'Entry', icon: BookOpen },
  ];

  return (
    <SectionTiles
      sections={SECTION_DEFS}
      activeSection={activeTab}
      onSelect={(v) => { setActiveTab(v); setSelectedCustIds([]); setSelectedUserIds([]); setSelectedEntryIds([]); setSelectedLedgerIds([]); }}
      pageTitle="Ledger"
      pageDescription="Customer, user, and entry ledgers"
      headerActions={(() => {
        if (activeTab === 'customer-ledger' && selectedCustomer) {
          return <ExportToolbar endpoint={`/exports/customer-ledger/${selectedCustomer}`} fileBaseName="customer-ledger" params={{ start_date: custDateRange.start_date, end_date: custDateRange.end_date }} testId="export-customer-ledger" />;
        }
        if (activeTab === 'supplier-ledger' && selectedSupplier) {
          return <ExportToolbar endpoint={`/exports/supplier-ledger/${selectedSupplier}`} fileBaseName="supplier-ledger" params={{ start_date: supplierDateRange.start_date, end_date: supplierDateRange.end_date }} testId="export-supplier-ledger" />;
        }
        return null;
      })()}
    >

        {/* Customer Ledger */}
      {activeTab === 'customer-ledger' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Label className="shrink-0 text-sm">Customer</Label>
            <div className="max-w-xs w-full">
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomer}
                onChange={handleCustomerSelect}
                placeholder="Select a customer"
                searchPlaceholder="Search customer..."
                data-testid="ledger-customer-select"
              />
            </div>
          </div>

          <DateRangeFilter
            startDate={custDateRange.start_date}
            endDate={custDateRange.end_date}
            onStartDateChange={(v) => setCustDateRange(prev => ({...prev, start_date: v}))}
            onEndDateChange={(v) => setCustDateRange(prev => ({...prev, end_date: v}))}
            onApply={handleCustDateApply}
            activeQuick={custQuick}
            onQuickSelect={setCustQuick}
            data-testid="cust-ledger-date"
          />

          {ledgerLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : customerLedger && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Opening Balance</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono">{formatCurrency(customerLedger.summary.opening_balance)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Sales</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-expense">{formatCurrency(customerLedger.summary.total_sales)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Payments</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-income">{formatCurrency(customerLedger.summary.total_payments)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Closing Balance</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className={`text-lg font-bold font-mono ${customerLedger.summary.closing_balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(customerLedger.summary.closing_balance)}</p></CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {customerLedger.customer.name}
                    <span className="text-sm font-normal text-muted-foreground ml-2">(Credit: {customerLedger.customer.credit_days} days)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10 cursor-pointer" onClick={toggleAllCust}><Checkbox checked={allCustSelected} className="pointer-events-none" data-testid="select-all-cust-ledger" /></TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Debit (Sale)</TableHead>
                          <TableHead className="text-right">Credit (Payment)</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {custLedgerEntries.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No transactions found</TableCell>
                          </TableRow>
                        ) : (
                          <>
                            <TableRow className="bg-muted/30 font-semibold" data-testid="customer-ledger-opening-row">
                              <TableCell />
                              <TableCell colSpan={3}>Opening Balance</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className={`text-right font-mono font-semibold ${customerLedger.summary.opening_balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(customerLedger.summary.opening_balance)}</TableCell>
                            </TableRow>
                            {custLedgerWithFY.map((entry, idx) =>
                              entry._fyBoundary ? (
                                <TableRow key={`fy-${idx}`} className="bg-amber-50 dark:bg-amber-950/30 border-y-2 border-amber-300 dark:border-amber-700" data-testid={`cust-fy-${entry._boundaryType}-${idx}`}>
                                  <TableCell />
                                  <TableCell colSpan={3} className="font-semibold text-amber-800 dark:text-amber-300">
                                    {entry._boundaryType === 'closing' ? `Closing Balance — ${entry._fyLabel}` : `Opening Balance — ${entry._fyLabel}`}
                                  </TableCell>
                                  <TableCell className="text-right font-mono">-</TableCell>
                                  <TableCell className="text-right font-mono">-</TableCell>
                                  <TableCell className={`text-right font-mono font-semibold ${entry.balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(entry.balance)}</TableCell>
                                </TableRow>
                              ) : (
                                <TableRow key={idx} className={selectedCustIds.includes(entry.reference_id) ? 'bg-muted/50' : ''}>
                                  <TableCell className="cursor-pointer" onClick={() => toggleCustSelect(entry.reference_id)}><Checkbox checked={selectedCustIds.includes(entry.reference_id)} className="pointer-events-none" data-testid={`select-cust-entry-${idx}`} /></TableCell>
                                  <TableCell>{formatDate(entry.date)}</TableCell>
                                  <TableCell><Badge variant={entry.type === 'sale' ? 'secondary' : 'default'}>{entry.type}</Badge></TableCell>
                                  <TableCell className="max-w-[200px] truncate">{entry.description}</TableCell>
                                  <TableCell className="text-right font-mono">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</TableCell>
                                  <TableCell className="text-right font-mono text-income">{entry.credit > 0 ? formatCurrency(entry.credit) : '-'}</TableCell>
                                  <TableCell className={`text-right font-mono font-semibold ${entry.balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(entry.balance)}</TableCell>
                                </TableRow>
                              )
                            )}
                            <TableRow className="bg-muted/30 font-semibold" data-testid="customer-ledger-closing-row">
                              <TableCell />
                              <TableCell colSpan={3}>Closing Balance</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className={`text-right font-mono font-semibold ${customerLedger.summary.closing_balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(customerLedger.summary.closing_balance)}</TableCell>
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

        {/* User Ledger */}
      {activeTab === 'user-ledger' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Label className="shrink-0 text-sm">User</Label>
            <div className="max-w-xs w-full">
              <SearchableSelect
                options={userOptions}
                value={selectedUser}
                onChange={handleUserSelect}
                placeholder="Select a user"
                searchPlaceholder="Search user..."
                data-testid="ledger-user-select"
              />
            </div>
          </div>

          <DateRangeFilter
            startDate={userDateRange.start_date}
            endDate={userDateRange.end_date}
            onStartDateChange={(v) => setUserDateRange(prev => ({...prev, start_date: v}))}
            onEndDateChange={(v) => setUserDateRange(prev => ({...prev, end_date: v}))}
            onApply={handleUserDateApply}
            activeQuick={userQuick}
            onQuickSelect={setUserQuick}
            data-testid="user-ledger-date"
          />

          {userLedgerLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : userLedger && (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Opening Balance</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono" data-testid="user-ledger-opening">{formatCurrency(userLedger.summary.opening_balance)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Income</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-income" data-testid="user-ledger-total-income">{formatCurrency(userLedger.summary.total_income)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Transfer In</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-income">{formatCurrency(userLedger.summary.transfers_in)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Expense</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-expense" data-testid="user-ledger-total-expense">{formatCurrency(userLedger.summary.total_expense)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Transfer Out</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-expense">{formatCurrency(userLedger.summary.transfers_out)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Closing Balance</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className={`text-lg font-bold font-mono ${userLedger.summary.closing_balance >= 0 ? 'text-income' : 'text-expense'}`} data-testid="user-ledger-closing">{formatCurrency(userLedger.summary.closing_balance)}</p></CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {userLedger.user.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10 cursor-pointer" onClick={toggleAllUser}><Checkbox checked={allUserSelected} className="pointer-events-none" data-testid="select-all-user-ledger" /></TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Debit</TableHead>
                          <TableHead className="text-right">Credit</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {userLedgerEntries.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-8 text-muted-foreground">No transactions found</TableCell>
                          </TableRow>
                        ) : (
                          <>
                            <TableRow className="bg-muted/30 font-semibold" data-testid="user-ledger-opening-row">
                              <TableCell />
                              <TableCell colSpan={4}>Opening Balance</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className={`text-right font-mono font-semibold ${userLedger.summary.opening_balance >= 0 ? 'text-income' : 'text-expense'}`}>{formatCurrency(userLedger.summary.opening_balance)}</TableCell>
                              {isAdmin && <TableCell />}
                            </TableRow>
                            {userLedgerWithFY.map((entry, idx) =>
                              entry._fyBoundary ? (
                                <TableRow key={`fy-${idx}`} className="bg-amber-50 dark:bg-amber-950/30 border-y-2 border-amber-300 dark:border-amber-700" data-testid={`user-fy-${entry._boundaryType}-${idx}`}>
                                  <TableCell />
                                  <TableCell colSpan={4} className="font-semibold text-amber-800 dark:text-amber-300">
                                    {entry._boundaryType === 'closing' ? `Closing Balance — ${entry._fyLabel}` : `Opening Balance — ${entry._fyLabel}`}
                                  </TableCell>
                                  <TableCell className="text-right font-mono">-</TableCell>
                                  <TableCell className="text-right font-mono">-</TableCell>
                                  <TableCell className={`text-right font-mono font-semibold ${entry.balance >= 0 ? 'text-income' : 'text-expense'}`}>{formatCurrency(entry.balance)}</TableCell>
                                  {isAdmin && <TableCell />}
                                </TableRow>
                              ) : (
                                <TableRow key={idx} className={selectedUserIds.includes(entry.reference_id) ? 'bg-muted/50' : ''}>
                                  <TableCell className="cursor-pointer" onClick={() => toggleUserSelect(entry.reference_id)}><Checkbox checked={selectedUserIds.includes(entry.reference_id)} className="pointer-events-none" data-testid={`select-user-entry-${idx}`} /></TableCell>
                                  <TableCell>{formatDate(entry.date)}</TableCell>
                                  <TableCell>
                                    <Badge variant={entry.type === 'income' ? 'default' : entry.type === 'expense' ? 'destructive' : 'secondary'}>
                                      {entry.type.replace('_', ' ')}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>{entry.category || '-'}</TableCell>
                                  <TableCell className="max-w-[200px] truncate">{entry.description}</TableCell>
                                  <TableCell className="text-right font-mono">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</TableCell>
                                  <TableCell className="text-right font-mono text-income">{entry.credit > 0 ? formatCurrency(entry.credit) : '-'}</TableCell>
                                  <TableCell className={`text-right font-mono font-semibold ${entry.balance >= 0 ? 'text-income' : 'text-expense'}`}>{formatCurrency(entry.balance)}</TableCell>
                                  {isAdmin && (
                                    <TableCell className="text-right">
                                      {(entry.type === 'transfer_in' || entry.type === 'transfer_out') && entry.entry_id && (
                                        <div className="flex justify-end gap-1">
                                          <Button variant="ghost" size="icon" onClick={() => openTransferEdit(entry)} data-testid={`edit-transfer-${entry.entry_id}`}><Pencil className="h-4 w-4" /></Button>
                                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleTransferDelete(entry)} data-testid={`delete-transfer-${entry.entry_id}`}><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                      )}
                                    </TableCell>
                                  )}
                                </TableRow>
                              )
                            )}
                            <TableRow className="bg-muted/30 font-semibold" data-testid="user-ledger-closing-row">
                              <TableCell />
                              <TableCell colSpan={4}>Closing Balance</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className={`text-right font-mono font-semibold ${userLedger.summary.closing_balance >= 0 ? 'text-income' : 'text-expense'}`}>{formatCurrency(userLedger.summary.closing_balance)}</TableCell>
                              {isAdmin && <TableCell />}
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

        {/* Supplier Ledger */}
      {activeTab === 'supplier-ledger' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Label className="shrink-0 text-sm">Supplier</Label>
            <div className="max-w-xs w-full">
              <SearchableSelect
                options={supplierOptions}
                value={selectedSupplier}
                onChange={handleSupplierSelect}
                placeholder="Select a supplier"
                searchPlaceholder="Search supplier..."
                data-testid="ledger-supplier-select"
              />
            </div>
          </div>

          <DateRangeFilter
            startDate={supplierDateRange.start_date}
            endDate={supplierDateRange.end_date}
            onStartDateChange={(v) => setSupplierDateRange(prev => ({...prev, start_date: v}))}
            onEndDateChange={(v) => setSupplierDateRange(prev => ({...prev, end_date: v}))}
            onApply={handleSupplierDateApply}
            activeQuick={supplierQuick}
            onQuickSelect={setSupplierQuick}
            data-testid="supplier-ledger-date"
          />

          {supplierLedgerLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : supplierLedger && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Opening Balance</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono">{formatCurrency(supplierLedger.summary.opening_balance)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Purchase</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-expense">{formatCurrency(supplierLedger.summary.total_purchase)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Payments</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-income">{formatCurrency(supplierLedger.summary.total_paid)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Closing Balance</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className={`text-lg font-bold font-mono ${supplierLedger.summary.closing_balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(supplierLedger.summary.closing_balance)}</p></CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {supplierLedger.supplier.name}
                    <span className="text-sm font-normal text-muted-foreground ml-2">(Credit: {supplierLedger.supplier.credit_days} days)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10 cursor-pointer" onClick={toggleAllSupp}><Checkbox checked={allSuppSelected} className="pointer-events-none" data-testid="select-all-supp-ledger" /></TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Debit (Purchase)</TableHead>
                          <TableHead className="text-right">Credit (Payment)</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {supplierLedgerEntries.length === 0 ? (
                          <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No transactions found</TableCell></TableRow>
                        ) : (
                          <>
                            <TableRow className="bg-muted/30 font-semibold">
                              <TableCell />
                              <TableCell colSpan={3}>Opening Balance</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className={`text-right font-mono font-semibold ${supplierLedger.summary.opening_balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(supplierLedger.summary.opening_balance)}</TableCell>
                            </TableRow>
                            {supplierLedgerWithFY.map((entry, idx) =>
                              entry._fyBoundary ? (
                                <TableRow key={`fy-${idx}`} className="bg-amber-50 dark:bg-amber-950/30 border-y-2 border-amber-300 dark:border-amber-700">
                                  <TableCell />
                                  <TableCell colSpan={3} className="font-semibold text-amber-800 dark:text-amber-300">
                                    {entry._boundaryType === 'closing' ? `Closing Balance — ${entry._fyLabel}` : `Opening Balance — ${entry._fyLabel}`}
                                  </TableCell>
                                  <TableCell className="text-right font-mono">-</TableCell>
                                  <TableCell className="text-right font-mono">-</TableCell>
                                  <TableCell className={`text-right font-mono font-semibold ${entry.balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(entry.balance)}</TableCell>
                                </TableRow>
                              ) : (
                                <TableRow key={idx} className={selectedSuppIds.includes(entry.reference_id) ? 'bg-muted/50' : ''}>
                                  <TableCell className="cursor-pointer" onClick={() => toggleSuppSelect(entry.reference_id)}><Checkbox checked={selectedSuppIds.includes(entry.reference_id)} className="pointer-events-none" data-testid={`select-supp-entry-${idx}`} /></TableCell>
                                  <TableCell className="whitespace-nowrap">{formatDate(entry.date)}</TableCell>
                                  <TableCell><Badge variant={entry.type === 'purchase' ? 'secondary' : 'default'}>{entry.type}</Badge></TableCell>
                                  <TableCell className="max-w-[200px] truncate">{entry.description}</TableCell>
                                  <TableCell className="text-right font-mono whitespace-nowrap">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</TableCell>
                                  <TableCell className="text-right font-mono text-income whitespace-nowrap">{entry.credit > 0 ? formatCurrency(entry.credit) : '-'}</TableCell>
                                  <TableCell className={`text-right font-mono font-semibold whitespace-nowrap ${entry.balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(entry.balance)}</TableCell>
                                </TableRow>
                              )
                            )}
                            <TableRow className="bg-muted/30 font-semibold">
                              <TableCell />
                              <TableCell colSpan={3}>Closing Balance</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className={`text-right font-mono font-semibold ${supplierLedger.summary.closing_balance > 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(supplierLedger.summary.closing_balance)}</TableCell>
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

        {/* Entry Ledger */}
      {activeTab === 'entry-ledger' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Entry Ledger
                </CardTitle>
                <Button size="sm" onClick={openCreateEntryLedger} data-testid="add-entry-ledger-btn">
                  <Plus className="h-4 w-4 mr-1" />Add Entry Ledger
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category Filter */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant={!selectedEntryLedgerCategory ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs h-7 px-3"
                  onClick={() => handleCategoryFilter('')}
                  data-testid="entry-ledger-filter-all"
                >
                  All
                </Button>
                {entryLedgerCategories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedEntryLedgerCategory === cat ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs h-7 px-3"
                    onClick={() => handleCategoryFilter(cat)}
                    data-testid={`entry-ledger-filter-${cat}`}
                  >
                    {cat}
                  </Button>
                ))}
              </div>

              {/* Category Report Summary */}
              {categoryReport && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-md bg-muted/50 border border-border text-center">
                    <p className="text-xs text-muted-foreground">Total Receivable</p>
                    <p className="text-lg font-bold font-mono text-expense">{formatCurrency(categoryReport.summary.total_receivable)}</p>
                  </div>
                  <div className="p-3 rounded-md bg-muted/50 border border-border text-center">
                    <p className="text-xs text-muted-foreground">Total Received</p>
                    <p className="text-lg font-bold font-mono text-income">{formatCurrency(categoryReport.summary.total_received)}</p>
                  </div>
                  <div className="p-3 rounded-md bg-muted/50 border border-border text-center">
                    <p className="text-xs text-muted-foreground">Outstanding</p>
                    <p className="text-lg font-bold font-mono text-expense">{formatCurrency(categoryReport.summary.total_outstanding)}</p>
                  </div>
                </div>
              )}

              {/* Category Report Table */}
              {categoryReportLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : categoryReport && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleAllLedgers(); }}><Checkbox checked={allLedgersSelected} className="pointer-events-none" data-testid="select-all-entry-ledgers" /></TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Receivable</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryReport.ledgers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No entry ledgers found</TableCell>
                        </TableRow>
                      ) : (
                        categoryReport.ledgers.map(ledger => (
                          <TableRow
                            key={ledger.id}
                            className={`cursor-pointer hover:bg-muted/50 ${selectedEntryLedger === ledger.id ? 'bg-muted/60' : ''} ${focusedEntryLedger === ledger.id ? 'ring-2 ring-primary ring-inset bg-primary/10' : ''} ${selectedLedgerIds.includes(ledger.id) ? 'bg-muted/50' : ''}`}
                            onClick={() => { setFocusedEntryLedger(ledger.id); handleEntryLedgerSelect(ledger.id); }}
                            data-testid={`entry-ledger-row-${ledger.id}`}
                          >
                            <TableCell onClick={(e) => { e.stopPropagation(); toggleLedgerSelect(ledger.id); }}><Checkbox checked={selectedLedgerIds.includes(ledger.id)} className="pointer-events-none" data-testid={`select-ledger-${ledger.id}`} /></TableCell>
                            <TableCell className="font-medium">{ledger.name}</TableCell>
                            <TableCell className="text-muted-foreground">{ledger.category || '-'}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(ledger.receivable)}</TableCell>
                            <TableCell className="text-right font-mono text-income">{formatCurrency(ledger.received)}</TableCell>
                            <TableCell className={`text-right font-mono font-semibold ${ledger.outstanding > 0 ? 'text-expense' : ledger.outstanding < 0 ? 'text-income' : ''}`}>{formatCurrency(ledger.outstanding)}</TableCell>
                            <TableCell>
                              <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditEntryLedger(ledger)}><Pencil className="h-3 w-3" /></Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteEntryLedger(ledger)}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Selected Ledger Detail */}
          {selectedEntryLedger && (
            <DateRangeFilter
              startDate={entryLedgerDateRange.start_date}
              endDate={entryLedgerDateRange.end_date}
              onStartDateChange={(v) => setEntryLedgerDateRange(prev => ({...prev, start_date: v}))}
              onEndDateChange={(v) => setEntryLedgerDateRange(prev => ({...prev, end_date: v}))}
              onApply={handleEntryLedgerDateApply}
              activeQuick={entryLedgerQuick}
              onQuickSelect={setEntryLedgerQuick}
              data-testid="entry-ledger-date"
            />
          )}
          {entryLedgerLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : entryLedgerData && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Opening Balance</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className={`text-lg font-bold font-mono ${entryLedgerData.summary.opening_balance >= 0 ? 'text-expense' : 'text-income'}`} data-testid="entry-ledger-opening">{formatCurrency(Math.abs(entryLedgerData.summary.opening_balance))}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Receivable</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-expense" data-testid="entry-ledger-debit">{formatCurrency(entryLedgerData.summary.total_debit)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Total Received</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className="text-lg font-bold font-mono text-income" data-testid="entry-ledger-credit">{formatCurrency(entryLedgerData.summary.total_credit)}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-muted-foreground">Closing Balance</CardTitle></CardHeader>
                  <CardContent className="px-3 pb-3"><p className={`text-lg font-bold font-mono ${entryLedgerData.summary.closing_balance >= 0 ? 'text-expense' : 'text-income'}`} data-testid="entry-ledger-closing">{formatCurrency(Math.abs(entryLedgerData.summary.closing_balance))}</p></CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {entryLedgerData.ledger_info.name}
                    {entryLedgerData.ledger_info.category && <span className="text-sm font-normal text-muted-foreground ml-2">({entryLedgerData.ledger_info.category})</span>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10 cursor-pointer" onClick={toggleAllEntry}><Checkbox checked={allEntrySelected} className="pointer-events-none" data-testid="select-all-entry-ledger" /></TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Debit</TableHead>
                          <TableHead className="text-right">Credit</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entryLedgerEntries.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No transactions found</TableCell>
                          </TableRow>
                        ) : (
                          <>
                            <TableRow className="bg-muted/30 font-semibold" data-testid="entry-ledger-opening-row">
                              <TableCell />
                              <TableCell colSpan={5}>Opening Balance</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className={`text-right font-mono font-semibold ${entryLedgerData.summary.opening_balance >= 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(Math.abs(entryLedgerData.summary.opening_balance))}</TableCell>
                            </TableRow>
                            {entryLedgerWithFY.map((entry, idx) =>
                              entry._fyBoundary ? (
                                <TableRow key={`fy-${idx}`} className="bg-amber-50 dark:bg-amber-950/30 border-y-2 border-amber-300 dark:border-amber-700" data-testid={`entry-fy-${entry._boundaryType}-${idx}`}>
                                  <TableCell />
                                  <TableCell colSpan={5} className="font-semibold text-amber-800 dark:text-amber-300">
                                    {entry._boundaryType === 'closing' ? `Closing Balance — ${entry._fyLabel}` : `Opening Balance — ${entry._fyLabel}`}
                                  </TableCell>
                                  <TableCell className="text-right font-mono">-</TableCell>
                                  <TableCell className="text-right font-mono">-</TableCell>
                                  <TableCell className={`text-right font-mono font-semibold ${entry.balance >= 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(Math.abs(entry.balance))}</TableCell>
                                </TableRow>
                              ) : (
                                <TableRow key={idx} className={selectedEntryIds.includes(entry.reference_id) ? 'bg-muted/50' : ''}>
                                  <TableCell className="cursor-pointer" onClick={() => toggleEntrySelect(entry.reference_id)}><Checkbox checked={selectedEntryIds.includes(entry.reference_id)} className="pointer-events-none" data-testid={`select-entry-${idx}`} /></TableCell>
                                  <TableCell>{formatDate(entry.date)}</TableCell>
                                  <TableCell><Badge variant={entry.type === 'income' ? 'default' : 'destructive'}>{entry.type}</Badge></TableCell>
                                  <TableCell>{entry.category || '-'}</TableCell>
                                  <TableCell className="text-sm">{entry.user_name || '-'}</TableCell>
                                  <TableCell className="max-w-[200px] truncate">{entry.description}</TableCell>
                                  <TableCell className="text-right font-mono">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</TableCell>
                                  <TableCell className="text-right font-mono text-income">{entry.credit > 0 ? formatCurrency(entry.credit) : '-'}</TableCell>
                                  <TableCell className={`text-right font-mono font-semibold ${entry.balance >= 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(Math.abs(entry.balance))}</TableCell>
                                </TableRow>
                              )
                            )}
                            <TableRow className="bg-muted/30 font-semibold" data-testid="entry-ledger-closing-row">
                              <TableCell />
                              <TableCell colSpan={5}>Closing Balance</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className="text-right font-mono">-</TableCell>
                              <TableCell className={`text-right font-mono font-semibold ${entryLedgerData.summary.closing_balance >= 0 ? 'text-expense' : 'text-income'}`}>{formatCurrency(Math.abs(entryLedgerData.summary.closing_balance))}</TableCell>
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Selection bars */}
      {selectedLedgerIds.length > 0 && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedLedgerIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedLedgerIds([])} data-testid="clear-selection-btn">Clear</Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDeleteLedgers} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
        </div>
      )}
      {selectedCustIds.length > 0 && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedCustIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedCustIds([])} data-testid="clear-selection-btn">Clear</Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDeleteCust} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
        </div>
      )}
      {selectedUserIds.length > 0 && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedUserIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedUserIds([])} data-testid="clear-selection-btn">Clear</Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDeleteUser} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
        </div>
      )}
      {selectedEntryIds.length > 0 && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedEntryIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedEntryIds([])} data-testid="clear-selection-btn">Clear</Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDeleteEntry} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
        </div>
      )}
      {selectedSuppIds.length > 0 && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedSuppIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedSuppIds([])} data-testid="clear-selection-btn">Clear</Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDeleteSupp} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
        </div>
      )}

      {/* Entry Ledger Create/Edit Dialog */}
      <Dialog open={entryLedgerDialogOpen} onOpenChange={setEntryLedgerDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingEntryLedger ? 'Edit Entry Ledger' : 'Create Entry Ledger'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleEntryLedgerSubmit} className="space-y-4">
            {entryLedgerError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{entryLedgerError}</AlertDescription></Alert>}
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={entryLedgerForm.name} onChange={(e) => setEntryLedgerForm(prev => ({...prev, name: e.target.value}))} placeholder="e.g. Comhard" required data-testid="entry-ledger-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Input value={entryLedgerForm.category} onChange={(e) => setEntryLedgerForm(prev => ({...prev, category: e.target.value}))} placeholder="e.g. IT Services, Rent" data-testid="entry-ledger-category-input" />
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setEntryLedgerDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={entryLedgerSubmitting || !entryLedgerForm.name} data-testid="entry-ledger-submit-btn">{entryLedgerSubmitting ? 'Saving...' : editingEntryLedger ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Transfer</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleTransferUpdate} className="space-y-4">
            {transferError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{transferError}</AlertDescription></Alert>}
            <div className="space-y-2">
              <Label>From User</Label>
              <SearchableSelect options={userOptions} value={transferForm.from_user_id} onChange={(v) => setTransferForm(prev => ({...prev, from_user_id: v}))} placeholder="Select user" data-testid="transfer-from-select" />
            </div>
            <div className="space-y-2">
              <Label>To User</Label>
              <SearchableSelect options={userOptions} value={transferForm.to_user_id} onChange={(v) => setTransferForm(prev => ({...prev, to_user_id: v}))} placeholder="Select user" data-testid="transfer-to-select" />
            </div>
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input type="number" step="0.01" min="0" value={transferForm.amount} onChange={(e) => setTransferForm(prev => ({...prev, amount: e.target.value}))} required data-testid="transfer-amount-input" />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea value={transferForm.remarks} onChange={(e) => setTransferForm(prev => ({...prev, remarks: e.target.value}))} data-testid="transfer-remarks-input" />
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={transferSubmitting} data-testid="transfer-submit-btn">{transferSubmitting ? 'Saving...' : 'Update'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>
    </SectionTiles>
  );
};

export default Ledger;
